import {Component, inject, Injector, Input, OnDestroy, OnInit} from '@angular/core';
import {MatIconRegistry} from "@angular/material/icon";
import {DomSanitizer} from "@angular/platform-browser";
import {BasePageComponent} from "@core-components/page-content/base-page.component";
import {baseChartColors} from "@features/dashboards/chart-options/chart-colors.model";
import {
  ControlType,
  getTemplateComponent,
  templateComponents
} from "@features/leads/application-form-builder/add-questions/question-components.model";
import {QuestionStatOption, QuestionStats, QuestionStatsGroup, Survey} from "@features/leads/surveys/surveys.model";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {UtilsService} from "@shared-services/utils.service";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {get} from "lodash";
import {Subject} from "rxjs";
import {SurveyService} from "../../survey.service";

@Component({
  selector: 'app-survey-results',
  templateUrl: './survey-results.component.html',
  styleUrl: './survey-results.component.scss'
})
export class SurveyResultsComponent extends BasePageComponent implements OnInit, OnDestroy {
  @Input('survey') survey!: Survey;

  private _loader = inject(LoadingService);
  private _clearOnDestroy$ = new Subject<void>();

  protected loading$ = this._loader.loading$;

  protected selectedGroupByField: string | null = "ALL";
  protected groupByFieldOptions: { code: string, label: string }[] = [];

  protected ungroupedQuestionStats: QuestionStats[] = [];

  protected submissionStatGroups: QuestionStatsGroup[] = [];
  protected submissionStatGroupMap: Map<string, Map<string, QuestionStats>> = new Map();

  protected groupOptions: QuestionStatOption[] = [];

  pageSize = 5;
  paginatorMap: Map<string, {pageIndex: number, options: QuestionStatOption[]}> = new Map();

  protected groupedControlTypes: ControlType[] = [
    ControlType.SELECT,
    ControlType.CHOICE,
    ControlType.RANGE
  ];

  constructor(injector: Injector,
              public utils: UtilsService,
              public dateUtils: DateUtilsService,
              private surveyService: SurveyService,
              public loader: LoadingService,
              private domSanitizer: DomSanitizer,
              private matIconRegistry: MatIconRegistry) {
    super(injector);

    this.matIconRegistry.addSvgIcon('arrow_range',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/form-icons/arrow_range.svg'));
    this.matIconRegistry.addSvgIcon('attachment',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/form-icons/attachment.svg'));
    this.matIconRegistry.addSvgIcon('calendar_month',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/form-icons/calendar_month.svg'));
    this.matIconRegistry.addSvgIcon('format_size',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/form-icons/format_size.svg'));
    this.matIconRegistry.addSvgIcon('location_on',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/form-icons/location_on.svg'));
    this.matIconRegistry.addSvgIcon('pin',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/form-icons/pin.svg'));
    this.matIconRegistry.addSvgIcon('rule_folder',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/form-icons/rule_folder.svg'));
    this.matIconRegistry.addSvgIcon('title',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/form-icons/title.svg'));
    this.matIconRegistry.addSvgIcon('view_agenda',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/form-icons/view_agenda.svg'));
    this.matIconRegistry.addSvgIcon('view_agenda_2',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/form-icons/view_agenda_2.svg'));
    this.matIconRegistry.addSvgIcon('file_download',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/form-icons/file_download.svg'));
    this.matIconRegistry.addSvgIcon('email',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/form-icons/email.svg'));
    this.matIconRegistry.addSvgIcon('contact_page',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/form-icons/contact_page.svg'));

    this.initGroupFieldOptions();
  }

  ngOnInit() {
    this.loadSurveyResults();
  }

  ngOnDestroy(): void {
    this._clearOnDestroy$.next();
  }

  protected refreshResponseData() {
    this.selectedGroupByField = "ALL";
    this.loadSurveyResults()
  }

  protected updatedGroupResults() {
    const selectedGroup = this.ungroupedQuestionStats.filter(f => f.questionCode == this.selectedGroupByField);
    if (selectedGroup) {
      this.groupOptions = [...selectedGroup[0].options];
    }

    this.loadSurveyResults();
  }

  protected loadSurveyResults() {
    this.surveyService.getSubmissionStatistics(this.survey!.uuid!, this.selectedGroupByField!).subscribe({
      next: results => {
        this.submissionStatGroups = results;

        if (this.selectedGroupByField === "ALL") {
          this.ungroupedQuestionStats = results[0].questionStats.sort((a, b) => a.displayOrder - b.displayOrder);
          this.populateGroupFieldOptions();

          this.paginatorMap = new Map();
          this.ungroupedQuestionStats.forEach(ea => {
            if (!this.groupedControlTypes.includes(ea.controlType)) {
              this.paginatorMap.set(ea.questionCode, {pageIndex: 0, options: ea.options})
            }
          })
        } else {
          this.submissionStatGroupMap = new Map();
          this.submissionStatGroups.forEach(group => {
            this.submissionStatGroupMap.set(group.groupCode, new Map(group.questionStats.map(m => [m.questionCode, m])))
          })
        }
      },
      error: err => {
        console.log(err);
      }
    });
  }

  protected populateGroupFieldOptions() {
    this.initGroupFieldOptions();
    this.submissionStatGroups[0].questionStats.forEach(qs => {
      if (this.groupedControlTypes.includes(qs.controlType)) {
        this.groupByFieldOptions.push({code: qs.questionCode, label: qs.questionText});
      }
    })
  }

  protected initGroupFieldOptions(): void {
    this.groupByFieldOptions = [
      {code: 'ALL', label: 'None'},
    ];
  }

  get controlTypes() {
    return templateComponents;
  }

  controlIcon(control: ControlType): string {
    return getTemplateComponent(control).icon;
  }

  ratingBars(options: QuestionStatOption[], totalResponse: number, controlType: ControlType) {
    let optionsToDisplay = [...options];
    if (controlType == ControlType.RANGE) {
      optionsToDisplay = options.sort((a, b) => a.optionCode < b.optionCode ? -1 : 1);
      optionsToDisplay = options.slice(1, options.length);
    }
    return optionsToDisplay.map((m, i) => {
      const percentOfTotal = totalResponse > 0 ? Math.round(m.responseCount / totalResponse * 100) : 0;
      const color = i > baseChartColors().length ? baseChartColors()[i - baseChartColors().length] : baseChartColors()[i];
      return {key: m.optionCode, label: m.optionText, value: percentOfTotal, color: color};
    })
  }


  displayedQuestions(): QuestionStats[] {
    const res: QuestionStats[] = [];
    const selectedGroup = this.ungroupedQuestionStats.filter(f => f.questionCode == this.selectedGroupByField);
    const otherQuestions = this.ungroupedQuestionStats.filter(f => f.questionCode != this.selectedGroupByField);

    res.push(...selectedGroup);
    res.push(...otherQuestions);
    return res;
  }

  getPaginatedOptions(code: string): QuestionStatOption[] {
    const startIndex = this.paginatorMap.get(code)!.pageIndex * this.pageSize;
    return this.paginatorMap.get(code)!.options.slice(startIndex, startIndex + this.pageSize);
  }

  getLength(code: string): number {
    return this.paginatorMap.get(code)!.options.length;
  }

  onPageChange(event: any, code: string): void {
    this.paginatorMap.get(code)!.pageIndex = event.pageIndex;
  }

  protected readonly ControlType = ControlType;
  protected readonly get = get;
}
